<!-- DataTable.vue -->
<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { debounce } from 'lodash-es'

interface Column {
  key: string
  label: string
  sortable?: boolean
  filterable?: boolean
}

interface Props {
  columns: Column[]
  data: any[]
  initialSort?: { key: string; order: 'asc' | 'desc' }
  pageSize?: number
  searchable?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  pageSize: 10,
  searchable: true
})

const emit = defineEmits<{
  (e: 'sort', key: string, order: 'asc' | 'desc'): void
  (e: 'page', page: number): void
  (e: 'search', query: string): void
}>()

// Sorting
const sortKey = ref(props.initialSort?.key || '')
const sortOrder = ref<'asc' | 'desc'>(props.initialSort?.order || 'asc')

// Pagination
const currentPage = ref(1)
const totalPages = computed(() => Math.ceil(filteredData.value.length / props.pageSize))

// Search
const searchQuery = ref('')
const debouncedSearch = debounce((query: string) => {
  emit('search', query)
}, 300)

// Filtered and sorted data
const filteredData = computed(() => {
  let result = [...props.data]
  
  // Apply search filter
  if (searchQuery.value) {
    const query = searchQuery.value.toLowerCase()
    result = result.filter(item => 
      Object.values(item).some(value => 
        String(value).toLowerCase().includes(query)
      )
    )
  }
  
  // Apply sorting
  if (sortKey.value) {
    result.sort((a, b) => {
      const valueA = a[sortKey.value]
      const valueB = b[sortKey.value]
      
      if (typeof valueA === 'string') {
        return sortOrder.value === 'asc' 
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA)
      }
      
      return sortOrder.value === 'asc'
        ? valueA - valueB
        : valueB - valueA
    })
  }
  
  return result
})

// Paginated data
const paginatedData = computed(() => {
  const start = (currentPage.value - 1) * props.pageSize
  const end = start + props.pageSize
  return filteredData.value.slice(start, end)
})

const sortBy = (key: string) => {
  if (sortKey.value === key) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortKey.value = key
    sortOrder.value = 'asc'
  }
  emit('sort', key, sortOrder.value)
}

const previousPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--
    emit('page', currentPage.value)
  }
}

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++
    emit('page', currentPage.value)
  }
}

const handleSearch = () => {
  currentPage.value = 1
  debouncedSearch(searchQuery.value)
}

// Reset page when data changes
watch(() => props.data, () => {
  currentPage.value = 1
})

// Reset page when search changes
watch(searchQuery, () => {
  currentPage.value = 1
})
</script>

<template>
  <div class="data-table-container">
    <!-- Table Controls -->
    <div class="table-controls">
      <!-- Search -->
      <div v-if="searchable" class="table-search">
        <input 
          type="text" 
          v-model="searchQuery"
          placeholder="Search..."
          @input="handleSearch"
          class="search-input"
        />
      </div>
      
      <!-- Custom Filters Slot -->
      <div class="table-filters">
        <slot name="filters"></slot>
      </div>
    </div>
    
    <!-- Table -->
    <div class="table-responsive">
      <table class="data-table">
        <thead>
          <tr>
            <th 
              v-for="column in columns" 
              :key="column.key"
              :class="{ 
                sortable: column.sortable,
                active: sortKey === column.key
              }"
              @click="column.sortable && sortBy(column.key)"
            >
              {{ column.label }}
              <span 
                v-if="column.sortable && sortKey === column.key"
                class="sort-indicator"
              >
                {{ sortOrder === 'asc' ? '↑' : '↓' }}
              </span>
            </th>
          </tr>
        </thead>
        
        <tbody>
          <tr 
            v-for="(item, index) in paginatedData" 
            :key="index"
          >
            <td 
              v-for="column in columns" 
              :key="column.key"
            >
              <slot 
                :name="column.key" 
                :item="item"
                :value="item[column.key]"
              >
                {{ item[column.key] }}
              </slot>
            </td>
          </tr>
          
          <!-- Empty State -->
          <tr v-if="paginatedData.length === 0">
            <td :colspan="columns.length" class="empty-state">
              <slot name="empty">
                No data available
              </slot>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- Pagination -->
    <div class="pagination" v-if="totalPages > 1">
      <button 
        @click="previousPage" 
        :disabled="currentPage === 1"
        class="pagination-button"
      >
        Previous
      </button>
      
      <span class="pagination-info">
        Page {{ currentPage }} of {{ totalPages }}
      </span>
      
      <button 
        @click="nextPage"
        :disabled="currentPage === totalPages"
        class="pagination-button"
      >
        Next
      </button>
    </div>
  </div>
</template>

<style scoped>
.data-table-container {
  @apply w-full;
}

.table-controls {
  @apply flex flex-wrap gap-4 mb-4;
}

.table-search {
  @apply flex-1;
}

.search-input {
  @apply w-full px-4 py-2 border border-gray-300 rounded-lg;
}

.table-responsive {
  @apply overflow-x-auto;
}

.data-table {
  @apply min-w-full divide-y divide-gray-200;
}

.data-table thead {
  @apply bg-gray-50;
}

.data-table th {
  @apply px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider;
}

.data-table th.sortable {
  @apply cursor-pointer hover:bg-gray-100;
}

.data-table th.active {
  @apply bg-gray-100;
}

.data-table td {
  @apply px-6 py-4 whitespace-nowrap text-sm text-gray-900;
}

.sort-indicator {
  @apply ml-2 text-gray-400;
}

.empty-state {
  @apply text-center py-8 text-gray-500;
}

.pagination {
  @apply flex items-center justify-between mt-4;
}

.pagination-button {
  @apply px-4 py-2 border border-gray-300 rounded-lg 
         hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed;
}

.pagination-info {
  @apply text-sm text-gray-700;
}
</style>